"use client";

import { useEffect, useRef, useState } from "react";
import { Effect, EffectComposer, EffectPass, RenderPass } from "postprocessing";
import * as THREE from "three";

import { cn } from "@heroui/react";

/**
 * Dithered pixel field, rendered on a WebGL quad.
 *
 * Ported from React Bits' `PixelBlast` (itself after
 * github.com/zavalit/bayer-dithering-webgl-demo). Four things changed on the
 * way in, each because this runs behind a real page rather than in a demo box:
 *
 * 1. `visibilityRef` is actually wired. Upstream declares it and never writes
 *    to it, so `autoPauseOffscreen` is dead code and the render loop keeps a
 *    GPU busy for the whole time someone reads the rest of the page. An
 *    `IntersectionObserver` sets it here.
 * 2. `prefers-reduced-motion` renders a single frame and stops. The field
 *    still reads as texture held still, which is why freezing it works where
 *    hiding it would leave a blank band.
 * 3. Renderer construction is guarded. `new WebGLRenderer` throws where WebGL
 *    is unavailable or blocked, and an unguarded throw inside an effect takes
 *    the page down with it — a background must fail invisibly.
 * 4. The liquid-strength uniform is set on the uniform rather than on the
 *    `Effect` itself, which is where upstream writes it (so changing
 *    `liquidStrength` after mount did nothing).
 *
 * The stylesheet that ships with it is four declarations; they are Tailwind
 * classes on the container instead of a second CSS file.
 */

type TouchTexture = {
  texture: THREE.Texture;
  addTouch: (norm: { x: number; y: number }) => void;
  update: () => void;
  radiusScale: number;
};

const createTouchTexture = (): TouchTexture => {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context not available");
  ctx.fillStyle = "black";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const texture = new THREE.Texture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;

  type Point = { x: number; y: number; age: number; force: number; vx: number; vy: number };
  const trail: Point[] = [];
  let last: { x: number; y: number } | null = null;
  const maxAge = 64;
  let radius = 0.1 * size;
  const speed = 1 / maxAge;

  const clear = () => {
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const drawPoint = (p: Point) => {
    const pos = { x: p.x * size, y: (1 - p.y) * size };
    let intensity = 1;
    const easeOutSine = (t: number) => Math.sin((t * Math.PI) / 2);
    const easeOutQuad = (t: number) => -t * (t - 2);
    if (p.age < maxAge * 0.3) intensity = easeOutSine(p.age / (maxAge * 0.3));
    else intensity = easeOutQuad(1 - (p.age - maxAge * 0.3) / (maxAge * 0.7)) || 0;
    intensity *= p.force;
    const color = `${((p.vx + 1) / 2) * 255}, ${((p.vy + 1) / 2) * 255}, ${intensity * 255}`;
    const offset = size * 5;
    ctx.shadowOffsetX = offset;
    ctx.shadowOffsetY = offset;
    ctx.shadowBlur = radius;
    ctx.shadowColor = `rgba(${color},${0.22 * intensity})`;
    ctx.beginPath();
    ctx.fillStyle = "rgba(255,0,0,1)";
    ctx.arc(pos.x - offset, pos.y - offset, radius, 0, Math.PI * 2);
    ctx.fill();
  };

  return {
    texture,
    addTouch(norm) {
      let force = 0;
      let vx = 0;
      let vy = 0;
      if (last) {
        const dx = norm.x - last.x;
        const dy = norm.y - last.y;
        if (dx === 0 && dy === 0) return;
        const dd = dx * dx + dy * dy;
        const d = Math.sqrt(dd);
        vx = dx / (d || 1);
        vy = dy / (d || 1);
        force = Math.min(dd * 10000, 1);
      }
      last = { x: norm.x, y: norm.y };
      trail.push({ x: norm.x, y: norm.y, age: 0, force, vx, vy });
    },
    update() {
      clear();
      for (let i = trail.length - 1; i >= 0; i--) {
        const point = trail[i];
        const f = point.force * speed * (1 - point.age / maxAge);
        point.x += point.vx * f;
        point.y += point.vy * f;
        point.age++;
        if (point.age > maxAge) trail.splice(i, 1);
      }
      for (const point of trail) drawPoint(point);
      texture.needsUpdate = true;
    },
    set radiusScale(v: number) {
      radius = 0.1 * size * v;
    },
    get radiusScale() {
      return radius / (0.1 * size);
    },
  };
};

const createLiquidEffect = (texture: THREE.Texture, opts: { strength: number; freq: number }) =>
  new Effect(
    "LiquidEffect",
    `
    uniform sampler2D uTexture;
    uniform float uStrength;
    uniform float uTime;
    uniform float uFreq;

    void mainUv(inout vec2 uv) {
      vec4 tex = texture2D(uTexture, uv);
      float vx = tex.r * 2.0 - 1.0;
      float vy = tex.g * 2.0 - 1.0;
      float intensity = tex.b;
      float wave = 0.5 + 0.5 * sin(uTime * uFreq + intensity * 6.2831853);
      float amt = uStrength * intensity * wave;
      uv += vec2(vx, vy) * amt;
    }
    `,
    {
      uniforms: new Map<string, THREE.Uniform>([
        ["uTexture", new THREE.Uniform(texture)],
        ["uStrength", new THREE.Uniform(opts.strength)],
        ["uTime", new THREE.Uniform(0)],
        ["uFreq", new THREE.Uniform(opts.freq)],
      ]),
    },
  );

const SHAPE_MAP = { square: 0, circle: 1, triangle: 2, diamond: 3 } as const;

export type PixelBlastVariant = keyof typeof SHAPE_MAP;

const VERTEX_SRC = /* glsl */ `
void main() {
  gl_Position = vec4(position, 1.0);
}
`;

const FRAGMENT_SRC = /* glsl */ `
precision highp float;

uniform vec3  uColor;
uniform vec2  uResolution;
uniform float uTime;
uniform float uPixelSize;
uniform float uScale;
uniform float uDensity;
uniform float uPixelJitter;
uniform int   uEnableRipples;
uniform float uRippleSpeed;
uniform float uRippleThickness;
uniform float uRippleIntensity;
uniform float uEdgeFade;

uniform int   uShapeType;
const int SHAPE_SQUARE   = 0;
const int SHAPE_CIRCLE   = 1;
const int SHAPE_TRIANGLE = 2;
const int SHAPE_DIAMOND  = 3;

const int   MAX_CLICKS = 10;

uniform vec2  uClickPos  [MAX_CLICKS];
uniform float uClickTimes[MAX_CLICKS];

out vec4 fragColor;

float Bayer2(vec2 a) {
  a = floor(a);
  return fract(a.x / 2. + a.y * a.y * .75);
}
#define Bayer4(a) (Bayer2(.5*(a))*0.25 + Bayer2(a))
#define Bayer8(a) (Bayer4(.5*(a))*0.25 + Bayer2(a))

#define FBM_OCTAVES     5
#define FBM_LACUNARITY  1.25
#define FBM_GAIN        1.0

float hash11(float n){ return fract(sin(n)*43758.5453); }

float vnoise(vec3 p){
  vec3 ip = floor(p);
  vec3 fp = fract(p);
  float n000 = hash11(dot(ip + vec3(0.0,0.0,0.0), vec3(1.0,57.0,113.0)));
  float n100 = hash11(dot(ip + vec3(1.0,0.0,0.0), vec3(1.0,57.0,113.0)));
  float n010 = hash11(dot(ip + vec3(0.0,1.0,0.0), vec3(1.0,57.0,113.0)));
  float n110 = hash11(dot(ip + vec3(1.0,1.0,0.0), vec3(1.0,57.0,113.0)));
  float n001 = hash11(dot(ip + vec3(0.0,0.0,1.0), vec3(1.0,57.0,113.0)));
  float n101 = hash11(dot(ip + vec3(1.0,0.0,1.0), vec3(1.0,57.0,113.0)));
  float n011 = hash11(dot(ip + vec3(0.0,1.0,1.0), vec3(1.0,57.0,113.0)));
  float n111 = hash11(dot(ip + vec3(1.0,1.0,1.0), vec3(1.0,57.0,113.0)));
  vec3 w = fp*fp*fp*(fp*(fp*6.0-15.0)+10.0);
  float x00 = mix(n000, n100, w.x);
  float x10 = mix(n010, n110, w.x);
  float x01 = mix(n001, n101, w.x);
  float x11 = mix(n011, n111, w.x);
  float y0  = mix(x00, x10, w.y);
  float y1  = mix(x01, x11, w.y);
  return mix(y0, y1, w.z) * 2.0 - 1.0;
}

float fbm2(vec2 uv, float t){
  vec3 p = vec3(uv * uScale, t);
  float amp = 1.0;
  float freq = 1.0;
  float sum = 1.0;
  for (int i = 0; i < FBM_OCTAVES; ++i){
    sum  += amp * vnoise(p * freq);
    freq *= FBM_LACUNARITY;
    amp  *= FBM_GAIN;
  }
  return sum * 0.5 + 0.5;
}

float maskCircle(vec2 p, float cov){
  float r = sqrt(cov) * .25;
  float d = length(p - 0.5) - r;
  float aa = 0.5 * fwidth(d);
  return cov * (1.0 - smoothstep(-aa, aa, d * 2.0));
}

float maskTriangle(vec2 p, vec2 id, float cov){
  bool flip = mod(id.x + id.y, 2.0) > 0.5;
  if (flip) p.x = 1.0 - p.x;
  float r = sqrt(cov);
  float d  = p.y - r*(1.0 - p.x);
  float aa = fwidth(d);
  return cov * clamp(0.5 - d/aa, 0.0, 1.0);
}

float maskDiamond(vec2 p, float cov){
  float r = sqrt(cov) * 0.564;
  return step(abs(p.x - 0.49) + abs(p.y - 0.49), r);
}

void main(){
  float pixelSize = uPixelSize;
  vec2 fragCoord = gl_FragCoord.xy - uResolution * .5;
  float aspectRatio = uResolution.x / uResolution.y;

  vec2 pixelId = floor(fragCoord / pixelSize);
  vec2 pixelUV = fract(fragCoord / pixelSize);

  float cellPixelSize = 8.0 * pixelSize;
  vec2 cellId = floor(fragCoord / cellPixelSize);
  vec2 cellCoord = cellId * cellPixelSize;
  vec2 uv = cellCoord / uResolution * vec2(aspectRatio, 1.0);

  float base = fbm2(uv, uTime * 0.05);
  base = base * 0.5 - 0.65;

  float feed = base + (uDensity - 0.5) * 0.3;

  float speed     = uRippleSpeed;
  float thickness = uRippleThickness;
  const float dampT     = 1.0;
  const float dampR     = 10.0;

  if (uEnableRipples == 1) {
    for (int i = 0; i < MAX_CLICKS; ++i){
      vec2 pos = uClickPos[i];
      if (pos.x < 0.0) continue;
      float cellPixelSize = 8.0 * pixelSize;
      vec2 cuv = (((pos - uResolution * .5 - cellPixelSize * .5) / (uResolution))) * vec2(aspectRatio, 1.0);
      float t = max(uTime - uClickTimes[i], 0.0);
      float r = distance(uv, cuv);
      float waveR = speed * t;
      float ring  = exp(-pow((r - waveR) / thickness, 2.0));
      float atten = exp(-dampT * t) * exp(-dampR * r);
      feed = max(feed, ring * atten * uRippleIntensity);
    }
  }

  float bayer = Bayer8(fragCoord / uPixelSize) - 0.5;
  float bw = step(0.5, feed + bayer);

  float h = fract(sin(dot(floor(fragCoord / uPixelSize), vec2(127.1, 311.7))) * 43758.5453);
  float jitterScale = 1.0 + (h - 0.5) * uPixelJitter;
  float coverage = bw * jitterScale;
  float M;
  if      (uShapeType == SHAPE_CIRCLE)   M = maskCircle (pixelUV, coverage);
  else if (uShapeType == SHAPE_TRIANGLE) M = maskTriangle(pixelUV, pixelId, coverage);
  else if (uShapeType == SHAPE_DIAMOND)  M = maskDiamond(pixelUV, coverage);
  else                                   M = coverage;

  if (uEdgeFade > 0.0) {
    vec2 norm = gl_FragCoord.xy / uResolution;
    float edge = min(min(norm.x, norm.y), min(1.0 - norm.x, 1.0 - norm.y));
    float fade = smoothstep(0.0, uEdgeFade, edge);
    M *= fade;
  }

  vec3 color = uColor;

  // sRGB gamma correction: linear to sRGB for accurate color output.
  vec3 srgbColor = mix(
    color * 12.92,
    1.055 * pow(color, vec3(1.0 / 2.4)) - 0.055,
    step(0.0031308, color)
  );

  fragColor = vec4(srgbColor, M);
}
`;

const MAX_CLICKS = 10;

export interface PixelBlastProps {
  variant?: PixelBlastVariant;
  /** Base pixel size, scaled by DPI at render time. */
  pixelSize?: number;
  /** Any CSS color THREE can parse — hex or rgb(), *not* oklch(). */
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  antialias?: boolean;
  patternScale?: number;
  patternDensity?: number;
  liquid?: boolean;
  liquidStrength?: number;
  liquidRadius?: number;
  pixelSizeJitter?: number;
  enableRipples?: boolean;
  rippleIntensityScale?: number;
  rippleThickness?: number;
  rippleSpeed?: number;
  liquidWobbleSpeed?: number;
  /** Stop the render loop while the container is scrolled out of view. */
  autoPauseOffscreen?: boolean;
  speed?: number;
  transparent?: boolean;
  /** Fade distance from each edge, 0-1. */
  edgeFade?: number;
  noiseAmount?: number;
}

type ThreeState = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.OrthographicCamera;
  material: THREE.ShaderMaterial;
  quad: THREE.Mesh;
  uniforms: Record<string, THREE.IUniform>;
  resizeObserver: ResizeObserver;
  intersectionObserver?: IntersectionObserver;
  raf: number;
  clickIx: number;
  composer?: EffectComposer;
  touch?: TouchTexture;
  liquidEffect?: Effect;
};

export function PixelBlast({
  variant = "square",
  pixelSize = 3,
  color = "#B497CF",
  className,
  style,
  antialias = true,
  patternScale = 2,
  patternDensity = 1,
  liquid = false,
  liquidStrength = 0.1,
  liquidRadius = 1,
  pixelSizeJitter = 0,
  enableRipples = true,
  rippleIntensityScale = 1,
  rippleThickness = 0.1,
  rippleSpeed = 0.3,
  liquidWobbleSpeed = 4.5,
  autoPauseOffscreen = true,
  speed = 0.5,
  transparent = true,
  edgeFade = 0.5,
  noiseAmount = 0,
}: PixelBlastProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(true);
  const speedRef = useRef(speed);
  const threeRef = useRef<ThreeState | null>(null);
  const prevConfigRef = useRef<{ antialias: boolean; liquid: boolean; noiseAmount: number } | null>(null);

  // Not `useReducedMotion()` from motion/react: this decides whether a WebGL
  // context is created at all, so it has to be known before the effect body
  // that creates it — and a state flip re-runs that effect, which is exactly
  // the wanted behaviour if the preference changes mid-visit.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    speedRef.current = speed;

    const cfg = { antialias, liquid, noiseAmount };
    let mustReinit = !threeRef.current;
    if (!mustReinit && prevConfigRef.current) {
      for (const key of ["antialias", "liquid", "noiseAmount"] as const) {
        if (prevConfigRef.current[key] !== cfg[key]) {
          mustReinit = true;
          break;
        }
      }
    }

    const teardown = (state: ThreeState) => {
      state.resizeObserver.disconnect();
      state.intersectionObserver?.disconnect();
      cancelAnimationFrame(state.raf);
      state.quad.geometry.dispose();
      state.material.dispose();
      state.composer?.dispose();
      state.renderer.dispose();
      state.renderer.forceContextLoss();
      if (state.renderer.domElement.parentElement === container) {
        container.removeChild(state.renderer.domElement);
      }
    };

    if (mustReinit) {
      if (threeRef.current) {
        teardown(threeRef.current);
        threeRef.current = null;
      }

      let renderer: THREE.WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({
          canvas: document.createElement("canvas"),
          antialias,
          alpha: true,
          powerPreference: "high-performance",
        });
      } catch {
        // No WebGL, or the context was refused. The page is not allowed to
        // care: this layer is decoration and its absence is a valid state.
        return;
      }

      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      container.appendChild(renderer.domElement);
      if (transparent) renderer.setClearAlpha(0);
      else renderer.setClearColor(0x000000, 1);

      const uniforms: Record<string, THREE.IUniform> = {
        uResolution: { value: new THREE.Vector2(0, 0) },
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(color) },
        uClickPos: { value: Array.from({ length: MAX_CLICKS }, () => new THREE.Vector2(-1, -1)) },
        uClickTimes: { value: new Float32Array(MAX_CLICKS) },
        uShapeType: { value: SHAPE_MAP[variant] ?? 0 },
        uPixelSize: { value: pixelSize * renderer.getPixelRatio() },
        uScale: { value: patternScale },
        uDensity: { value: patternDensity },
        uPixelJitter: { value: pixelSizeJitter },
        uEnableRipples: { value: enableRipples ? 1 : 0 },
        uRippleSpeed: { value: rippleSpeed },
        uRippleThickness: { value: rippleThickness },
        uRippleIntensity: { value: rippleIntensityScale },
        uEdgeFade: { value: edgeFade },
      };

      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      const material = new THREE.ShaderMaterial({
        vertexShader: VERTEX_SRC,
        fragmentShader: FRAGMENT_SRC,
        uniforms,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        glslVersion: THREE.GLSL3,
      });
      const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
      scene.add(quad);
      const clock = new THREE.Clock();

      const setSize = () => {
        const w = container.clientWidth || 1;
        const h = container.clientHeight || 1;
        renderer.setSize(w, h, false);
        uniforms.uResolution.value.set(renderer.domElement.width, renderer.domElement.height);
        threeRef.current?.composer?.setSize(renderer.domElement.width, renderer.domElement.height);
        uniforms.uPixelSize.value = pixelSize * renderer.getPixelRatio();
      };
      setSize();
      const resizeObserver = new ResizeObserver(setSize);
      resizeObserver.observe(container);

      // A random phase so two instances on one page do not pulse in lockstep.
      const timeOffset = Math.random() * 1000;

      let composer: EffectComposer | undefined;
      let touch: TouchTexture | undefined;
      let liquidEffect: Effect | undefined;
      /** Effects whose `uTime` has to be advanced each frame. */
      const timedEffects: Effect[] = [];

      if (liquid) {
        touch = createTouchTexture();
        touch.radiusScale = liquidRadius;
        composer = new EffectComposer(renderer);
        liquidEffect = createLiquidEffect(touch.texture, {
          strength: liquidStrength,
          freq: liquidWobbleSpeed,
        });
        const effectPass = new EffectPass(camera, liquidEffect);
        effectPass.renderToScreen = true;
        composer.addPass(new RenderPass(scene, camera));
        composer.addPass(effectPass);
        timedEffects.push(liquidEffect);
      }

      if (noiseAmount > 0) {
        if (!composer) {
          composer = new EffectComposer(renderer);
          composer.addPass(new RenderPass(scene, camera));
        }
        const noiseEffect = new Effect(
          "NoiseEffect",
          `uniform float uTime; uniform float uAmount; float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453);} void mainUv(inout vec2 uv){} void mainImage(const in vec4 inputColor,const in vec2 uv,out vec4 outputColor){ float n=hash(floor(uv*vec2(1920.0,1080.0))+floor(uTime*60.0)); float g=(n-0.5)*uAmount; outputColor=inputColor+vec4(vec3(g),0.0);} `,
          {
            uniforms: new Map<string, THREE.Uniform>([
              ["uTime", new THREE.Uniform(0)],
              ["uAmount", new THREE.Uniform(noiseAmount)],
            ]),
          },
        );
        const noisePass = new EffectPass(camera, noiseEffect);
        for (const pass of composer.passes) pass.renderToScreen = false;
        noisePass.renderToScreen = true;
        composer.addPass(noisePass);
        timedEffects.push(noiseEffect);
      }

      composer?.setSize(renderer.domElement.width, renderer.domElement.height);

      const mapToPixels = (e: PointerEvent) => {
        const rect = renderer.domElement.getBoundingClientRect();
        const scaleX = renderer.domElement.width / rect.width;
        const scaleY = renderer.domElement.height / rect.height;
        return {
          fx: (e.clientX - rect.left) * scaleX,
          fy: (rect.height - (e.clientY - rect.top)) * scaleY,
          w: renderer.domElement.width,
          h: renderer.domElement.height,
        };
      };

      const onPointerDown = (e: PointerEvent) => {
        const { fx, fy } = mapToPixels(e);
        const ix = threeRef.current?.clickIx ?? 0;
        (uniforms.uClickPos.value as THREE.Vector2[])[ix].set(fx, fy);
        (uniforms.uClickTimes.value as Float32Array)[ix] = uniforms.uTime.value;
        if (threeRef.current) threeRef.current.clickIx = (ix + 1) % MAX_CLICKS;
      };
      renderer.domElement.addEventListener("pointerdown", onPointerDown, { passive: true });

      // Only bound when something consumes it. Upstream registers this
      // unconditionally and bails inside the handler, which means a listener
      // firing on every mouse movement across the section to do nothing.
      if (touch) {
        const activeTouch = touch;
        renderer.domElement.addEventListener(
          "pointermove",
          (e: PointerEvent) => {
            const { fx, fy, w, h } = mapToPixels(e);
            activeTouch.addTouch({ x: fx / w, y: fy / h });
          },
          { passive: true },
        );
      }

      const drawFrame = () => {
        if (composer) {
          touch?.update();
          // The effects created above, not a walk over `composer.passes`:
          // upstream searches every pass for a `uTime` uniform each frame,
          // which is a scan to find two objects already in scope here.
          for (const effect of timedEffects) {
            const u = effect.uniforms.get("uTime");
            if (u) u.value = uniforms.uTime.value;
          }
          composer.render();
        } else {
          renderer.render(scene, camera);
        }
      };

      // Upstream declares a visibility ref and never writes to it, so the loop
      // runs forever. This background sits at the top of a long page: without
      // the observer it keeps a GPU at full tilt while someone reads the
      // sections below it, which on a laptop is audible.
      let intersectionObserver: IntersectionObserver | undefined;
      if (autoPauseOffscreen) {
        intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            visibleRef.current = entry.isIntersecting;
          },
          { rootMargin: "100px" },
        );
        intersectionObserver.observe(container);
      }

      let raf = 0;
      if (reduceMotion) {
        // One frame, then nothing. The dither pattern is legible held still,
        // so freezing keeps the texture instead of leaving an empty band.
        uniforms.uTime.value = timeOffset;
        drawFrame();
      } else {
        const animate = () => {
          raf = requestAnimationFrame(animate);
          if (threeRef.current) threeRef.current.raf = raf;
          if (autoPauseOffscreen && !visibleRef.current) return;
          uniforms.uTime.value = timeOffset + clock.getElapsedTime() * speedRef.current;
          drawFrame();
        };
        raf = requestAnimationFrame(animate);
      }

      threeRef.current = {
        renderer,
        scene,
        camera,
        material,
        quad,
        uniforms,
        resizeObserver,
        intersectionObserver,
        raf,
        clickIx: 0,
        composer,
        touch,
        liquidEffect,
      };
    } else if (threeRef.current) {
      const t = threeRef.current;
      t.uniforms.uShapeType.value = SHAPE_MAP[variant] ?? 0;
      t.uniforms.uPixelSize.value = pixelSize * t.renderer.getPixelRatio();
      (t.uniforms.uColor.value as THREE.Color).set(color);
      t.uniforms.uScale.value = patternScale;
      t.uniforms.uDensity.value = patternDensity;
      t.uniforms.uPixelJitter.value = pixelSizeJitter;
      t.uniforms.uEnableRipples.value = enableRipples ? 1 : 0;
      t.uniforms.uRippleIntensity.value = rippleIntensityScale;
      t.uniforms.uRippleThickness.value = rippleThickness;
      t.uniforms.uRippleSpeed.value = rippleSpeed;
      t.uniforms.uEdgeFade.value = edgeFade;
      if (transparent) t.renderer.setClearAlpha(0);
      else t.renderer.setClearColor(0x000000, 1);
      // Upstream writes `liquidStrength` onto the Effect rather than onto its
      // uniform, so the prop silently did nothing after the first mount.
      const uStrength = t.liquidEffect?.uniforms.get("uStrength");
      if (uStrength) uStrength.value = liquidStrength;
      const uFreq = t.liquidEffect?.uniforms.get("uFreq");
      if (uFreq) uFreq.value = liquidWobbleSpeed;
      if (t.touch) t.touch.radiusScale = liquidRadius;
    }

    prevConfigRef.current = cfg;

    return () => {
      if (!mustReinit || !threeRef.current) return;
      teardown(threeRef.current);
      threeRef.current = null;
    };
  }, [
    antialias,
    liquid,
    noiseAmount,
    pixelSize,
    patternScale,
    patternDensity,
    enableRipples,
    rippleIntensityScale,
    rippleThickness,
    rippleSpeed,
    pixelSizeJitter,
    edgeFade,
    transparent,
    liquidStrength,
    liquidRadius,
    liquidWobbleSpeed,
    autoPauseOffscreen,
    reduceMotion,
    variant,
    color,
    speed,
  ]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className={cn("relative size-full overflow-hidden", className)}
      style={style}
    />
  );
}

/**
 * The field as a section background: positioned, masked, behind the content.
 *
 * Both call sites need the same three things right — `absolute inset-0`, the
 * `-z-10` that keeps it behind the column, and the `.fx-pixel-field` mask —
 * and getting any of them wrong is the difference between wallpaper and a
 * rectangle sitting on top of the page. The tuning stays at the call site,
 * because a hero and a closing line do not want the same density.
 *
 * `className` lands on the wrapper, not on the canvas: opacity is what varies
 * between the two, and it has to apply to the masked layer.
 */
export function PixelField({ className, ...blast }: PixelBlastProps) {
  return (
    <div className={cn("fx-pixel-field absolute inset-0 -z-10", className)}>
      <PixelBlast {...blast} />
    </div>
  );
}
